import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  useSubItemCategories,
  useAddSubItemCategory,
  useUpdateSubItemCategory,
  useDeleteSubItemCategory,
} from '@/hooks/useSubItemCategories';
import {
  useLaborRatePresets,
  useAddLaborRatePreset,
  useUpdateLaborRatePreset,
  useDeleteLaborRatePreset,
} from '@/hooks/useLaborRatePresets';
import type { SubItemCategory, LaborRatePreset } from '@jobos/shared';
import { getUnitLabel, DEFAULT_UNIT_LABELS } from '@jobos/shared';
import { UnitLabelInput } from '@/components/shared/UnitLabelInput';

// --- Inline preset editor for a single category ---
// A preset carries its own free-text unit label (e.g. "sq ft", "hr"). Different
// presets in the same category can use different units.

function CategoryPresets({ categoryKey }: { categoryKey: string }) {
  const { data: allPresets = [] } = useLaborRatePresets();
  const addMutation = useAddLaborRatePreset();
  const updateMutation = useUpdateLaborRatePreset();
  const deleteMutation = useDeleteLaborRatePreset();

  const presets = useMemo(
    () => allPresets.filter((p) => p.categoryKey === categoryKey),
    [allPresets, categoryKey]
  );

  // Suggestions: common defaults + labels actually chosen on presets (skip
  // null-unit presets so the display fallback doesn't leak in as a suggestion).
  const unitSuggestions = useMemo(() => {
    const used = allPresets.filter((p) => p.unitKey).map((p) => getUnitLabel({ unitKey: p.unitKey }));
    return Array.from(new Set([...DEFAULT_UNIT_LABELS, ...used]));
  }, [allPresets]);

  const presetUnitLabel = (p: LaborRatePreset) =>
    getUnitLabel({ unitKey: p.unitKey, rateType: p.rateType });

  const [newLabel, setNewLabel] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newLabel.trim();
    const parsed = parseFloat(newRate);
    if (!trimmed || isNaN(parsed) || parsed < 0) return;
    addMutation.mutate(
      { label: trimmed, rate: parsed, categoryKey, unitKey: newUnit.trim() || undefined },
      {
        onSuccess: () => {
          setNewLabel('');
          setNewRate('');
          setNewUnit('');
        },
      }
    );
  };

  const handleStartEdit = (item: LaborRatePreset) => {
    setEditingId(item.id);
    setEditLabel(item.label);
    setEditRate(item.rate.toFixed(2));
    setEditUnit(presetUnitLabel(item));
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    const trimmed = editLabel.trim();
    const parsed = parseFloat(editRate);
    if (!trimmed || isNaN(parsed) || parsed < 0) return;
    updateMutation.mutate({ id: editingId, label: trimmed, rate: parsed, unitKey: editUnit.trim() || null });
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeletingId(null);
  };

  return (
    <div className="pl-8 pr-4 pb-3 pt-1 bg-gray-50/50">
      {presets.map((item) => (
        <div key={item.id} className="flex items-center justify-between py-1.5 group">
          {editingId === item.id ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                className="max-w-48 h-7 text-sm"
                autoFocus
              />
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-xs text-gray-400">/</span>
                <div className="w-24">
                  <UnitLabelInput
                    value={editUnit}
                    onChange={setEditUnit}
                    suggestions={unitSuggestions}
                    className="h-7 text-xs"
                  />
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={handleSaveEdit} className="h-6 w-6 p-0">
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-6 w-6 p-0">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : deletingId === item.id ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xs text-red-600">Delete "{item.label}"?</span>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="h-6 text-xs">
                Confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)} className="h-6 text-xs">
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <span className="text-sm text-gray-700">
                {item.label} — <span className="text-gray-500">${item.rate.toFixed(2)}/{presetUnitLabel(item)}</span>
              </span>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" onClick={() => handleStartEdit(item)} className="h-6 w-6 p-0">
                  <Pencil className="h-3 w-3 text-gray-500" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingId(item.id)} className="h-6 w-6 p-0">
                  <Trash2 className="h-3 w-3 text-gray-500" />
                </Button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* Add preset form */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          placeholder="Preset name"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="max-w-48 h-7 text-sm"
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-xs text-gray-400">/</span>
          <div className="w-24">
            <UnitLabelInput
              value={newUnit}
              onChange={setNewUnit}
              suggestions={unitSuggestions}
              className="h-7 text-xs"
            />
          </div>
        </div>
        <Button
          onClick={handleAdd}
          disabled={!newLabel.trim() || !newRate || isNaN(parseFloat(newRate)) || addMutation.isPending}
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}

// --- Main page ---

export default function SubItemCategoriesSettings() {
  const { data: categories = [], isLoading } = useSubItemCategories();
  const { data: allPresets = [] } = useLaborRatePresets();
  const addMutation = useAddSubItemCategory();
  const updateMutation = useUpdateSubItemCategory();
  const deleteMutation = useDeleteSubItemCategory();

  const [newLabel, setNewLabel] = useState('');
  const [newPricingMode, setNewPricingMode] = useState<'qty_rate' | 'amount'>('amount');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editPricingMode, setEditPricingMode] = useState<'qty_rate' | 'amount'>('amount');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const presetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of allPresets) {
      counts[p.categoryKey] = (counts[p.categoryKey] || 0) + 1;
    }
    return counts;
  }, [allPresets]);

  const handleAdd = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    addMutation.mutate({
      label: trimmed,
      pricingMode: newPricingMode,
    }, {
      onSuccess: () => {
        setNewLabel('');
        setNewPricingMode('amount');
      },
    });
  };

  const handleStartEdit = (cat: SubItemCategory) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditPricingMode(cat.pricingMode);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editLabel.trim()) return;
    updateMutation.mutate({
      id: editingId,
      label: editLabel.trim(),
      pricingMode: editPricingMode,
    });
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeletingId(null);
  };

  const toggleExpand = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Sub-Item Categories</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Sub-Item Categories</h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure sub-item categories for job breakdowns. Qty x Rate categories can have rate presets — expand them to manage presets.
      </p>

      {/* Add new category */}
      <div className="flex gap-2 mb-6">
        <Input
          placeholder="Category name (e.g. Roofing, HVAC)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="max-w-sm"
        />
        <div className="inline-flex rounded-md border border-gray-300 text-sm overflow-hidden">
          <button
            type="button"
            className={`px-3 py-1.5 whitespace-nowrap ${newPricingMode === 'amount' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setNewPricingMode('amount')}
          >
            Amount
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 whitespace-nowrap ${newPricingMode === 'qty_rate' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setNewPricingMode('qty_rate')}
          >
            Qty x Rate
          </button>
        </div>
        <Button
          onClick={handleAdd}
          disabled={!newLabel.trim() || addMutation.isPending}
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Categories list */}
      <div className="border rounded-lg divide-y">
        {categories.map((cat) => {
          const isQtyRate = cat.pricingMode === 'qty_rate';
          const isExpanded = expandedKey === cat.key;
          const count = presetCounts[cat.key] || 0;
          // Discount and Profit are flat-amount built-ins — their pricing mode is
          // locked to Amount (flipping them to Qty x Rate breaks breakdown saves).
          const isModeLocked = cat.key === 'discount' || cat.key === 'profit';

          return (
            <div key={cat.id}>
              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group">
                {editingId === cat.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      className="max-w-sm h-8"
                      autoFocus
                    />
                    {isModeLocked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-sm text-gray-500 border border-gray-200 rounded-md bg-gray-50">
                        <Lock className="h-3 w-3" />
                        Amount
                      </span>
                    ) : (
                      <div className="inline-flex rounded-md border border-gray-300 text-sm overflow-hidden">
                        <button
                          type="button"
                          className={`px-2 py-0.5 ${editPricingMode === 'amount' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          onClick={() => setEditPricingMode('amount')}
                        >
                          Amount
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-0.5 ${editPricingMode === 'qty_rate' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          onClick={() => setEditPricingMode('qty_rate')}
                        >
                          Qty x Rate
                        </button>
                      </div>
                    )}
                    <Button size="sm" variant="ghost" onClick={handleSaveEdit} disabled={!editLabel.trim() || updateMutation.isPending}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : deletingId === cat.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-red-600">Delete "{cat.label}"?</span>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(cat.id)}
                      disabled={deleteMutation.isPending}
                    >
                      Confirm
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      {isQtyRate ? (
                        <button
                          type="button"
                          onClick={() => toggleExpand(cat.key)}
                          className="p-0.5 -ml-1 rounded hover:bg-gray-200"
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4 text-gray-500" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />
                          }
                        </button>
                      ) : (
                        <span className="w-5" />
                      )}
                      <span className="text-sm text-gray-900 font-medium">{cat.label}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                        isQtyRate
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isQtyRate ? 'Qty x Rate' : 'Amount'}
                      </span>
                      {cat.isBuiltIn && (
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                          <Lock className="h-3 w-3" />
                          Built-in
                        </span>
                      )}
                      {isQtyRate && count > 0 && (
                        <span className="text-[10px] text-gray-400">
                          {count} preset{count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {cat.key !== 'profit' && cat.key !== 'discount' && (
                        <>
                          <label className="flex items-center gap-1.5 mr-2 cursor-pointer">
                            <span className="text-[11px] text-gray-500">
                              Tax: {cat.taxApplicable ? 'On' : 'Off'}
                            </span>
                            <Switch
                              checked={cat.taxApplicable}
                              onCheckedChange={(checked) =>
                                updateMutation.mutate({ id: cat.id, taxApplicable: checked })
                              }
                            />
                          </label>
                          <label className="flex items-center gap-1.5 mr-2 cursor-pointer">
                            <span className="text-[11px] text-gray-500">
                              Markup: {cat.markupApplicable ? 'On' : 'Off'}
                            </span>
                            <Switch
                              checked={cat.markupApplicable}
                              onCheckedChange={(checked) =>
                                updateMutation.mutate({ id: cat.id, markupApplicable: checked })
                              }
                            />
                          </label>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(cat)}
                        className="h-7 w-7 p-0"
                      >
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                      {cat.isBuiltIn ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled
                          className="h-7 w-7 p-0"
                          title="Cannot delete built-in categories"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-gray-300" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeletingId(cat.id)}
                          className="h-7 w-7 p-0"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
              {isQtyRate && isExpanded && (
                <CategoryPresets categoryKey={cat.key} />
              )}
            </div>
          );
        })}
        {categories.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No categories yet. Add one above.
          </div>
        )}
      </div>
    </div>
  );
}
